// src/hooks/useDesignColors.ts
import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';
import { useChatbot } from '../contexts/ChatbotContext';
import { useEffect } from 'react';

interface DesignColors {
  gradientStart: string;
  gradientEnd: string;
}

const DEFAULT_COLORS: DesignColors = {
  gradientStart: '#7C3AED',
  gradientEnd: '#2DD4BF',
};

interface DesignColorsStore {
  colors: DesignColors;
  loading: boolean;
  error: Error | null;
  setColors: (colors: Partial<DesignColors>, chatbotPublicId?: string) => Promise<boolean>;
  initializeColors: (chatbotPublicId?: string) => Promise<void>;
}

export const useDesignColors = create<DesignColorsStore>((set, get) => ({
  colors: DEFAULT_COLORS,
  loading: false,
  error: null,
  
  setColors: async (newColors, chatbotPublicId) => {
    set({ loading: true, error: null });
    
    try {
      // Get current colors to merge with new colors
      const currentColors = get().colors;
      const updatedColors = { ...currentColors, ...newColors };
      
      // Update colors in the database
      const success = await updateChatbotConfigSection<DesignColors>(
        'colors',
        updatedColors,
        chatbotPublicId
      );
      
      if (success) {
        set({ colors: updatedColors });
      }
      
      set({ loading: false });
      return success;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error setting colors');
      console.error('Error saving colors:', error);
      set({ error, loading: false });
      return false;
    }
  },
  
  initializeColors: async (chatbotPublicId) => {
    set({ loading: true, error: null });
    
    try {
      // Get colors from the database
      const colors = await getChatbotConfigSection<DesignColors>(
        'colors',
        chatbotPublicId
      );
      
      // Use retrieved colors or defaults
      set({ 
        colors: colors || DEFAULT_COLORS,
        loading: false 
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error initializing colors');
      console.error('Error initializing colors:', error);
      set({ error, loading: false });
    }
  }
}));

// Hook pour charger les couleurs du chatbot quand il change
export function useLoadColorsOnChatbotChange() {
  const { chatbotPublicId } = useChatbot();
  const { initializeColors } = useDesignColors();

  useEffect(() => {
    if (chatbotPublicId) {
      initializeColors(chatbotPublicId);
    }
  }, [chatbotPublicId, initializeColors]);
}
